/**
 * Outil « Règle à calcul » (Boîte à outils, Tle).
 *
 * Deux réglettes graduées en échelle logarithmique : la longueur de 1 à x est
 * proportionnelle à ln x. On fait glisser la réglette C (au doigt ou aux
 * flèches) et on pose le curseur : D lit le produit, car les longueurs
 * s'ajoutent (ln a + ln b = ln ab) ; deux barres montrent ces longueurs.
 * « Placer la règle » montre les gestes pour a × b ou a ÷ b (le 10 de la
 * réglette quand le produit dépasse 10). Défi : poser soi-même la règle,
 * l'outil dit quoi corriger. Logique : `slide.ts`.
 */

import { useEffect, useMemo, useRef, useState, type KeyboardEvent, type PointerEvent } from 'react';
import { cn } from '@jeraisonne/ui';
import { Note, PRIMARY_BUTTON, SECONDARY_BUTTON, Segmented, Stepper } from '../../ui/controls';
import { frenchSpaces } from '../../ui/format';
import { seededRng } from '../../ui/random';
import { FIGURE_BG_CLASS, HaloText } from '../../ui/svg-parts';
import {
  GRID,
  SHIFTS,
  check,
  cursorStops,
  explain,
  fmt,
  indexOf,
  makeRound,
  opText,
  place,
  pos,
  readout,
  reading,
  show,
  snapCursor,
  snapShift,
  stepIn,
  verdictText,
  type Op,
} from './slide';

const W = 340;
const H = 196;
const X0 = 20;
const L = 300;
const C_TOP = 18;
const C_BOT = 58;
const D_TOP = 62;
const D_BOT = 102;
const BAR_Y = 150;
const ROUNDS = 5;

/** abscisse écran d'une position en décades */
const X = (p: number) => X0 + p * L;

const reduceMotion = () =>
  typeof window !== 'undefined' &&
  !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/** graduations : 1 → 10, grandes aux entiers, moyennes aux demis */
const TICKS = GRID.map((v) => ({
  v,
  size: Number.isInteger(v) ? 14 : Number.isInteger(v * 2) ? 9 : 5,
  label: Number.isInteger(v) || v === 1.5 ? fmt(v) : null,
}));

/** valeurs posables par a et b (sans 10) */
const VALUES = GRID.filter((v) => v < 10);

function Rule({
  s,
  u,
  animate,
  onShift,
  onCursor,
  onGrab,
}: {
  s: number;
  u: number;
  animate: boolean;
  onShift: (s: number) => void;
  onCursor: (u: number) => void;
  onGrab: () => void;
}) {
  const svgRef = useRef<SVGSVGElement>(null);
  const grab = useRef<{ kind: 'slide' | 'cursor'; x0: number; s0: number } | null>(null);
  const transition = animate && !reduceMotion() ? 'transform 600ms ease-out' : 'none';
  const at = (e: PointerEvent<SVGSVGElement>) => {
    const r = svgRef.current?.getBoundingClientRect();
    if (!r || !r.width) return null;
    return { x: ((e.clientX - r.left) / r.width) * W, y: ((e.clientY - r.top) / r.height) * H };
  };
  const down = (e: PointerEvent<SVGSVGElement>) => {
    const p = at(e);
    if (!p) return;
    onGrab();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    // la réglette C se prend sur sa bande ; ailleurs, on pose le curseur
    if (p.y >= C_TOP - 6 && p.y <= C_BOT) grab.current = { kind: 'slide', x0: p.x, s0: s };
    else {
      grab.current = { kind: 'cursor', x0: p.x, s0: s };
      onCursor(snapCursor(s, (p.x - X0) / L));
    }
  };
  const move = (e: PointerEvent<SVGSVGElement>) => {
    const g = grab.current;
    const p = at(e);
    if (!g || !p) return;
    if (g.kind === 'slide') onShift(snapShift(g.s0 + (p.x - g.x0) / L));
    else onCursor(snapCursor(s, (p.x - X0) / L));
  };
  const up = () => {
    grab.current = null;
  };
  const idx = indexOf(s);
  const r = reading(s, u);
  const lenBars = s >= -1e-9 && u >= s - 1e-9 && r.c !== null;

  return (
    <svg
      ref={svgRef}
      viewBox={`0 0 ${W} ${H}`}
      className={cn(
        'border-ink-100 mx-auto block w-full max-w-[28rem] touch-none select-none rounded-2xl border',
        FIGURE_BG_CLASS,
      )}
      role="group"
      aria-label="Règle à calcul : la réglette C glisse sur la règle D ; le curseur lit les deux."
      data-testid="sr-figure"
      onPointerDown={down}
      onPointerMove={move}
      onPointerUp={up}
      onPointerCancel={up}
    >
      {/* règle fixe D */}
      <rect
        x={X(0)}
        y={D_TOP}
        width={L}
        height={D_BOT - D_TOP}
        rx={4}
        fill="var(--color-ink-50)"
        stroke="var(--color-ink-300)"
      />
      <text x={6} y={D_TOP + 24} fontSize={12} fontWeight={700} fill="var(--color-ink-600)">
        D
      </text>
      {TICKS.map((t) => (
        <g key={`d${t.v}`}>
          <line
            x1={X(pos(t.v))}
            x2={X(pos(t.v))}
            y1={D_TOP}
            y2={D_TOP + t.size}
            stroke="var(--color-ink-700)"
            strokeWidth={t.size > 10 ? 1.4 : 1}
          />
          {t.label && (
            <text
              x={X(pos(t.v))}
              y={D_TOP + 28}
              textAnchor="middle"
              fontSize={12}
              fill="var(--color-ink-800)"
            >
              {t.label}
            </text>
          )}
        </g>
      ))}
      {/* réglette C, qui glisse */}
      <g>
        <g style={{ transform: `translateX(${s * L}px)`, transition }}>
          <rect
            x={X(0)}
            y={C_TOP}
            width={L}
            height={C_BOT - C_TOP}
            rx={4}
            fill="var(--color-primary-100)"
            stroke="var(--color-primary-400)"
            data-testid="sr-slide"
          />
          <text
            x={X(0) + 4}
            y={C_TOP + 12}
            fontSize={12}
            fontWeight={700}
            fill="var(--color-primary-700)"
          >
            C
          </text>
          {TICKS.map((t) => {
            const index = t.v === 1 || t.v === 10;
            return (
              <g key={`c${t.v}`}>
                <line
                  x1={X(pos(t.v))}
                  x2={X(pos(t.v))}
                  y1={C_BOT}
                  y2={C_BOT - (index ? C_BOT - C_TOP - 18 : t.size)}
                  stroke={index ? 'var(--color-accent-600)' : 'var(--color-ink-700)'}
                  strokeWidth={index ? 2.2 : t.size > 10 ? 1.4 : 1}
                />
                {t.label && (
                  <text
                    x={X(pos(t.v)) + (t.v === 1 ? 3 : t.v === 10 ? -3 : 0)}
                    y={C_BOT - 18}
                    textAnchor={t.v === 1 ? 'start' : t.v === 10 ? 'end' : 'middle'}
                    fontSize={12}
                    fontWeight={index ? 700 : 400}
                    fill={index ? 'var(--color-accent-700)' : 'var(--color-ink-800)'}
                  >
                    {t.label}
                  </text>
                )}
              </g>
            );
          })}
        </g>
      </g>
      {/* curseur : trait fin et poignée à toucher */}
      <g style={{ transform: `translateX(${u * L}px)`, transition }} data-testid="sr-cursor">
        <line
          x1={X(0)}
          x2={X(0)}
          y1={C_TOP - 8}
          y2={D_BOT + 8}
          stroke="var(--color-error-600)"
          strokeWidth={1.6}
        />
        <rect
          x={X(0) - 11}
          y={D_BOT + 8}
          width={22}
          height={26}
          rx={6}
          fill="var(--color-error-600)"
          opacity={0.85}
        />
        <line
          x1={X(0) - 4}
          x2={X(0) + 4}
          y1={D_BOT + 16}
          y2={D_BOT + 16}
          stroke="white"
          strokeWidth={1.5}
        />
        <line
          x1={X(0) - 4}
          x2={X(0) + 4}
          y1={D_BOT + 22}
          y2={D_BOT + 22}
          stroke="white"
          strokeWidth={1.5}
        />
      </g>
      {/* les longueurs qui s'ajoutent */}
      {lenBars && (
        <g data-testid="sr-lengths">
          <rect
            x={X(0)}
            y={BAR_Y}
            width={Math.max(0, s * L)}
            height={8}
            fill="var(--color-accent-500)"
          />
          <rect
            x={X(s)}
            y={BAR_Y}
            width={Math.max(0, (u - s) * L)}
            height={8}
            fill="var(--color-success-500)"
          />
          {s * L > 30 && (
            <HaloText
              x={X(s / 2)}
              y={BAR_Y + 24}
              textAnchor="middle"
              fontSize={12}
              fill="var(--color-ink-800)"
            >
              {`ln ${fmt(idx.on)}`}
            </HaloText>
          )}
          {(u - s) * L > 30 && (
            <HaloText
              x={X((s + u) / 2)}
              y={BAR_Y + 24}
              textAnchor="middle"
              fontSize={12}
              fill="var(--color-ink-800)"
            >
              {`ln ${show(r.c!).replace('≈ ', '')}`}
            </HaloText>
          )}
        </g>
      )}
    </svg>
  );
}

/** les deux réglages de la règle au clavier */
function Controls({
  s,
  u,
  onShift,
  onCursor,
}: {
  s: number;
  u: number;
  onShift: (s: number) => void;
  onCursor: (u: number) => void;
}) {
  const idx = indexOf(s);
  const r = reading(s, u);
  const key =
    (list: () => readonly number[], v: number, set: (x: number) => void) =>
    (e: KeyboardEvent<HTMLButtonElement>) => {
      const l = list();
      const d: Record<string, () => number> = {
        ArrowRight: () => stepIn(l, v, 1),
        ArrowUp: () => stepIn(l, v, 1),
        ArrowLeft: () => stepIn(l, v, -1),
        ArrowDown: () => stepIn(l, v, -1),
        Home: () => l[0]!,
        End: () => l[l.length - 1]!,
      };
      const f = d[e.key];
      if (!f) return;
      e.preventDefault();
      set(f());
    };
  const nudge = (list: readonly number[], v: number, dir: 1 | -1) => stepIn(list, v, dir);
  const btn =
    'border-ink-200 text-ink-700 hover:bg-ink-50 min-h-11 min-w-11 rounded-xl border text-lg font-bold active:scale-[0.98]';
  return (
    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
      <div className="border-ink-100 flex items-center gap-1 rounded-2xl border p-1">
        <button
          type="button"
          className={btn}
          aria-label="Réglette vers la gauche"
          onClick={() => onShift(nudge(SHIFTS, s, -1))}
        >
          ←
        </button>
        <button
          type="button"
          role="slider"
          aria-label="Réglette C"
          aria-valuemin={-1}
          aria-valuemax={1}
          aria-valuenow={Number(s.toFixed(4))}
          aria-valuetext={`son ${idx.mark} en face de ${fmt(idx.on)}`}
          onKeyDown={key(() => SHIFTS, s, onShift)}
          className="text-ink-800 min-h-11 flex-1 rounded-xl px-2 text-center text-sm"
        >
          {frenchSpaces(`Réglette : ${idx.mark} sur ${fmt(idx.on)}`)}
        </button>
        <button
          type="button"
          className={btn}
          aria-label="Réglette vers la droite"
          onClick={() => onShift(nudge(SHIFTS, s, 1))}
        >
          →
        </button>
      </div>
      <div className="border-ink-100 flex items-center gap-1 rounded-2xl border p-1">
        <button
          type="button"
          className={btn}
          aria-label="Curseur vers la gauche"
          onClick={() => onCursor(nudge(cursorStops(s), u, -1))}
        >
          ←
        </button>
        <button
          type="button"
          role="slider"
          aria-label="Curseur"
          aria-valuemin={0}
          aria-valuemax={1}
          aria-valuenow={Number(u.toFixed(4))}
          aria-valuetext={`${r.c ? `${show(r.c)} sur C, ` : ''}${show(r.d)} sur D`}
          onKeyDown={key(() => cursorStops(s), u, onCursor)}
          className="text-ink-800 min-h-11 flex-1 rounded-xl px-2 text-center text-sm"
        >
          {frenchSpaces(`Curseur : ${r.c ? `C ${show(r.c)}, ` : ''}D ${show(r.d)}`)}
        </button>
        <button
          type="button"
          className={btn}
          aria-label="Curseur vers la droite"
          onClick={() => onCursor(nudge(cursorStops(s), u, 1))}
        >
          →
        </button>
      </div>
    </div>
  );
}

function useRule(initial: { s: number; u: number }) {
  const [s, setS] = useState(initial.s);
  const [u, setU] = useState(initial.u);
  const [animate, setAnimate] = useState(false);
  return {
    s,
    u,
    animate,
    setShift: (v: number) => {
      setAnimate(false);
      setS(v);
    },
    setCursor: (v: number) => {
      setAnimate(false);
      setU(v);
    },
    placeAt: (p: { s: number; u: number }) => {
      setAnimate(true);
      setS(p.s);
      setU(p.u);
    },
    stopAnim: () => setAnimate(false),
  };
}

function Explore() {
  const [kind, setKind] = useState<'mul' | 'div'>('mul');
  const [ai, setAi] = useState(VALUES.indexOf(2));
  const [bi, setBi] = useState(VALUES.indexOf(3));
  const rule = useRule({ s: 0, u: pos(2) });
  const [shown, setShown] = useState<Op | null>(null);
  const op: Op = { kind, a: VALUES[ai]!, b: VALUES[bi]! };
  return (
    <div className="space-y-3">
      <Rule
        s={rule.s}
        u={rule.u}
        animate={rule.animate}
        onShift={(v) => {
          setShown(null);
          rule.setShift(v);
        }}
        onCursor={(v) => {
          setShown(null);
          rule.setCursor(v);
        }}
        onGrab={rule.stopAnim}
      />
      <Controls
        s={rule.s}
        u={rule.u}
        onShift={(v) => {
          setShown(null);
          rule.setShift(v);
        }}
        onCursor={(v) => {
          setShown(null);
          rule.setCursor(v);
        }}
      />
      <p className="text-ink-900 text-sm" aria-live="polite" data-testid="sr-readout">
        {frenchSpaces(readout(rule.s, rule.u))}
      </p>
      <section aria-label="Placer la règle pour un calcul" className="space-y-2">
        <Segmented
          ariaLabel="Opération"
          value={kind}
          options={[
            { value: 'mul', label: 'Multiplier' },
            { value: 'div', label: 'Diviser' },
          ]}
          onChange={(v) => {
            setKind(v);
            setShown(null);
          }}
        />
        <div className="grid grid-cols-2 gap-2">
          <Stepper
            label="a"
            value={ai}
            min={0}
            max={VALUES.length - 1}
            onChange={(v) => {
              setAi(v);
              setShown(null);
            }}
            format={(i) => fmt(VALUES[i]!)}
          />
          <Stepper
            label="b"
            value={bi}
            min={0}
            max={VALUES.length - 1}
            onChange={(v) => {
              setBi(v);
              setShown(null);
            }}
            format={(i) => fmt(VALUES[i]!)}
          />
        </div>
        <button
          type="button"
          className={PRIMARY_BUTTON}
          onClick={() => {
            rule.placeAt(place(op));
            setShown(op);
          }}
        >
          {frenchSpaces(`Placer la règle pour ${opText(op)}`)}
        </button>
        {shown && (
          <Note live>
            <span data-testid="sr-explain">{frenchSpaces(explain(shown))}</span>
          </Note>
        )}
      </section>
      <p className="text-ink-600 text-xs">
        {frenchSpaces(
          'La distance de la graduation 1 à la graduation x est proportionnelle à ln x : la règle entière mesure ln 10. Faire glisser la réglette ajoute des longueurs, donc multiplie les nombres : ln a + ln b = ln(ab). Glisse la réglette C (sa bande bleue) au doigt, touche ailleurs pour poser le curseur, ou utilise les flèches.',
        )}
      </p>
    </div>
  );
}

function Challenge({ seed }: { seed?: number }) {
  const rnd = useMemo(() => (seed === undefined ? Math.random : seededRng(seed)), [seed]);
  const [count, setCount] = useState(1);
  const [op, setOp] = useState<Op>(() => makeRound(0, rnd));
  const rule = useRule({ s: 0, u: 0 });
  const [verdict, setVerdict] = useState<{ ok: boolean; text: string } | null>(null);
  const [tries, setTries] = useState(0);
  const [score, setScore] = useState(0);
  const [solved, setSolved] = useState(false);
  const [revealed, setRevealed] = useState(false);
  const titleRef = useRef<HTMLParagraphElement>(null);
  const nextRef = useRef<HTMLButtonElement>(null);
  const moved = useRef(false);
  const done = solved && count >= ROUNDS;

  useEffect(() => {
    if (moved.current) titleRef.current?.focus();
    moved.current = false;
  }, [op]);
  // réussi : « Vérifier » disparaît, le focus passe à « Calcul suivant »
  useEffect(() => {
    if (solved) nextRef.current?.focus();
  }, [solved]);

  const verify = () => {
    const v = check(op, rule.s, rule.u);
    const ok = v === 'ok';
    setVerdict({ ok, text: verdictText(op, v) });
    if (ok) {
      if (!solved) setScore(score + 1);
      setSolved(true);
    } else setTries(tries + 1);
  };
  const reveal = () => {
    rule.placeAt(place(op));
    setVerdict({ ok: true, text: `Voici la règle posée. ${explain(op)}` });
    setRevealed(true);
    setSolved(true);
  };
  const next = () => {
    const c = done ? 1 : count + 1;
    if (done) setScore(0);
    setCount(c);
    moved.current = true;
    setOp(makeRound(c - 1, rnd));
    setVerdict(null);
    setTries(0);
    setSolved(false);
    setRevealed(false);
    rule.placeAt({ s: 0, u: 0 });
  };
  return (
    <div className="space-y-3">
      <p className="text-ink-500 text-xs font-semibold uppercase tracking-wide">
        {frenchSpaces(`Calcul ${count} / ${ROUNDS} · ${score} juste${score > 1 ? 's' : ''}`)}
      </p>
      <p ref={titleRef} tabIndex={-1} className="text-ink-900 text-base font-semibold outline-none">
        {frenchSpaces(
          `Calcule ${opText(op)} avec la règle : place la réglette et le curseur, puis vérifie.`,
        )}
      </p>
      <Rule
        s={rule.s}
        u={rule.u}
        animate={rule.animate}
        onShift={(v) => {
          if (!solved) setVerdict(null);
          rule.setShift(v);
        }}
        onCursor={(v) => {
          if (!solved) setVerdict(null);
          rule.setCursor(v);
        }}
        onGrab={rule.stopAnim}
      />
      <Controls
        s={rule.s}
        u={rule.u}
        onShift={(v) => {
          if (!solved) setVerdict(null);
          rule.setShift(v);
        }}
        onCursor={(v) => {
          if (!solved) setVerdict(null);
          rule.setCursor(v);
        }}
      />
      <p className="text-ink-900 text-sm" data-testid="sr-readout">
        {frenchSpaces(readout(rule.s, rule.u, false))}
      </p>
      {!solved && (
        <div className="flex flex-wrap gap-2">
          <button type="button" className={PRIMARY_BUTTON} onClick={verify}>
            Vérifier
          </button>
          {tries >= 2 && (
            <button type="button" className={SECONDARY_BUTTON} onClick={reveal}>
              Voir la solution
            </button>
          )}
        </div>
      )}
      {verdict && (
        <Note live tone={revealed ? 'neutral' : verdict.ok ? 'success' : 'error'}>
          <span data-testid="sr-verdict">{frenchSpaces(verdict.text)}</span>
        </Note>
      )}
      {solved && (
        <div className="flex flex-wrap items-center gap-3">
          {done && (
            <p className="text-ink-900 text-sm font-semibold">
              {frenchSpaces(`Score : ${score} / ${ROUNDS}`)}
            </p>
          )}
          <button
            ref={nextRef}
            type="button"
            className={done ? SECONDARY_BUTTON : PRIMARY_BUTTON}
            onClick={next}
          >
            {done ? 'Recommencer' : 'Calcul suivant'}
          </button>
        </div>
      )}
    </div>
  );
}

export function SlideRuleTool({ seed }: { /** graine du défi (tests) */ seed?: number }) {
  const [mode, setMode] = useState<'explore' | 'quiz'>('explore');
  return (
    <div className="space-y-4">
      <Segmented
        ariaLabel="Ce qu’on fait"
        value={mode}
        options={[
          { value: 'explore', label: 'Explorer' },
          { value: 'quiz', label: 'Défi' },
        ]}
        onChange={setMode}
      />
      <div hidden={mode !== 'explore'}>
        <Explore />
      </div>
      <div hidden={mode !== 'quiz'}>
        <Challenge seed={seed} />
      </div>
    </div>
  );
}
